// FILE: src/components/Admin/AI/kb/UploadDialog.tsx
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import FileDropper from '../../../Common/FileDropper';
import ManagedUploadStatusPanel from './ManagedUploadStatusPanel';

type Props = {
  open: boolean;
  pipelineId: string;
  pipelineName?: string;
  onClose: () => void;
  onUpload: (files: File[]) => Promise<any>;
  uploading?: boolean;
  taskId?: string | null;
  executionId?: string | null;
  uploadStatus?: any;
  ingestionStatus?: any;
  managedUploadStatus?: any;
};

const UploadDialog: React.FC<Props> = ({
                                         open,
                                         pipelineId,
                                         pipelineName,
                                         onClose,
                                         onUpload,
                                         uploading,
                                         taskId,
                                         executionId,
                                         uploadStatus,
                                         ingestionStatus,
                                         managedUploadStatus,
                                       }) => {
  const [uploadedCount, setUploadedCount] = React.useState(0);
  const [lastUploadResult, setLastUploadResult] = React.useState<any>(null);

  React.useEffect(() => {
    if (!open) {
      setUploadedCount(0);
      setLastUploadResult(null);
    }
  }, [open]);

  const uploadOne = async (payload: { file: File }) => {
    const resp = await onUpload([payload.file]);
    setUploadedCount((count) => count + 1);
    setLastUploadResult(resp);
    return resp;
  };

  const busy = !!uploading;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Upload Files to Selected DRIFT Pipeline</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {busy ? <LinearProgress /> : null}

          <Typography variant="body2" color="text.secondary">
            Managed upload updates the standby pipeline first, waits for the upload and ingestion to complete,
            waits for the pipeline to become active, temporarily cuts over, updates the original active pipeline,
            validates it, and then restores the original active slot.
          </Typography>

          <Box>
            <Typography variant="body2">
              Pipeline: <b>{pipelineName || pipelineId || 'backend default'}</b>
            </Typography>
            <Typography variant="body2">
              Pipeline ID: <b>{pipelineId || '-'}</b>
            </Typography>
            <Typography variant="body2">
              Upload Task: <b>{taskId || '-'}</b>
            </Typography>
            <Typography variant="body2">
              Ingestion Execution: <b>{executionId || '-'}</b>
            </Typography>
          </Box>

          {uploadStatus ? (
            <Typography variant="body2">
              Upload Status:{' '}
              <b>{String(uploadStatus.status || uploadStatus.current_status || 'unknown')}</b>
            </Typography>
          ) : null}

          {ingestionStatus ? (
            <Typography variant="body2">
              Ingestion:{' '}
              <b>
                {String(
                  ingestionStatus.current_status || ingestionStatus.status || 'unknown'
                )}
              </b>
              {ingestionStatus.current_stage ? ` (${ingestionStatus.current_stage})` : ''}
            </Typography>
          ) : null}

          <FileDropper
            title="Upload documents"
            description="Drag & drop files here, or click to select files."
            kbTag={pipelineId ? `kb.${pipelineId}` : undefined}
            accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,.csv,.json,.jsonl"
            allowedExtensions={[
              'pdf',
              'docx',
              'pptx',
              'xlsx',
              'txt',
              'md',
              'csv',
              'json',
              'jsonl',
            ]}
            upload={uploadOne}
            disabled={!!busy || !pipelineId}
            onUploaded={() => {}}
            onAllComplete={() => {}}
          />

          <Typography variant="body2" color="text.secondary">
            Uploaded in this dialog session: <b>{uploadedCount}</b>
          </Typography>

          <ManagedUploadStatusPanel
            status={managedUploadStatus || lastUploadResult}
            busy={busy}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;